import { getTreatmentBySlug, type Treatment } from "./treatments";

/**
 * By-need taxonomy. Groups the treatment catalogue by the concern a client
 * arrives with, rather than by the device or modality. Each concern lists its
 * treatment slugs flagship-first: the first slug is the treatment we lead with
 * for that need. Slugs resolve to full Treatment objects via treatments.ts.
 */
export interface Concern {
  slug: string;
  name: string;
  /** Treatment slugs, flagship-first. Ordering is meaningful and preserved. */
  treatmentSlugs: string[];
  /** Short factual lede. Placeholder copy pending voice pass. */
  intro: string;
}

/**
 * Signature treatments to emphasise wherever a concern surfaces them. Both are
 * South Island / latest-generation devices Silk leads on.
 */
export const FLAGSHIP_TREATMENT_SLUGS: readonly string[] = [
  "volnewmer-rf-skin-tightening",
  "ultraformer-mpt-hifu",
];

/** Nav label for the by-need menu. Nav order: About, Treatments, Your Concern, Pricing. */
export const CONCERN_NAV_LABEL = "Your Concern";

export const CONCERNS: Concern[] = [
  {
    slug: "fine-lines-wrinkles",
    name: "Fine Lines & Wrinkles",
    treatmentSlugs: [
      "anti-wrinkle-injections",
      "volnewmer-rf-skin-tightening",
      "ultraformer-mpt-hifu",
      "pdo-mono-threads",
      "skin-peels",
      "prp-vampire-facial",
    ],
    intro:
      "Treatments that soften dynamic and static lines across the forehead, eyes, and mouth.",
  },
  {
    slug: "volume-contour",
    name: "Volume Loss & Contour",
    treatmentSlugs: [
      "dermal-fillers",
      "lip-filler",
      "dermal-fillers-for-men",
      "combined-treatment-plans",
    ],
    intro:
      "Treatments that restore lost volume and redefine facial contours.",
  },
  {
    slug: "skin-laxity-lifting",
    name: "Skin Laxity & Lifting",
    treatmentSlugs: [
      "ultraformer-mpt-hifu",
      "volnewmer-rf-skin-tightening",
      "lifting-threads",
      "pdo-mono-threads",
    ],
    intro:
      "Treatments that firm and lift loose or sagging skin without surgery.",
  },
  {
    slug: "texture-dullness",
    name: "Texture & Dullness",
    treatmentSlugs: [
      "skin-peels",
      "microdermabrasion-sonophoresis",
      "prp-vampire-facial",
    ],
    intro:
      "Treatments that resurface and refine uneven skin texture and tone.",
  },
  {
    slug: "hydration-glow",
    name: "Hydration & Glow",
    treatmentSlugs: [
      "skin-hydration-injections",
      "prp-vampire-facial",
      "microdermabrasion-sonophoresis",
    ],
    intro:
      "Treatments that deliver hydration and improve skin radiance.",
  },
  {
    slug: "hair-loss",
    name: "Hair Loss",
    treatmentSlugs: ["prp-hair-loss"],
    intro: "Treatments that address thinning hair and support regrowth.",
  },
  {
    slug: "facial-slimming",
    name: "Facial Slimming",
    treatmentSlugs: ["fat-dissolver"],
    intro:
      "Treatments that reduce small pockets of fat to slim facial contours.",
  },
];

/** Resolve a concern by slug. Returns undefined if the slug is not known. */
export function getConcern(slug: string): Concern | undefined {
  return CONCERNS.find((concern) => concern.slug === slug);
}

/**
 * Resolve a concern's treatments to full Treatment objects, flagship-first
 * order preserved. Any slug that no longer maps to a treatment is skipped
 * gracefully so the taxonomy never breaks a page.
 */
export function getConcernTreatments(slug: string): Treatment[] {
  const concern = getConcern(slug);
  if (!concern) return [];
  return concern.treatmentSlugs
    .map((treatmentSlug) => getTreatmentBySlug(treatmentSlug))
    .filter((treatment): treatment is Treatment => treatment !== undefined);
}

/** True when a treatment slug is one of the signature flagship treatments. */
export function isFlagshipTreatment(slug: string): boolean {
  return FLAGSHIP_TREATMENT_SLUGS.includes(slug);
}
